import { VALIDATION_RULES } from "./validationRules";

/**
 * Validate a single field based on its rules
 * @param {string} name - Field name
 * @param {string} value - Field value
 * @param {object} customRules - Optional custom rules for this field
 * @param {object} formData - All form data (for matchField validation)
 * @returns {string} Error message or empty string
 */
export const validateField = (
  name,
  value,
  customRules = null,
  formData = {}
) => {
  const rule = customRules || VALIDATION_RULES[name];
  if (!rule) return "";

  // Required validation
  if (rule.required && typeof rule.required === "string" && !value.trimStart()) {
    return rule.required;
  }

  // Skip other validations if field is empty and not required
  if (!value.trimStart() && !rule.required) {
    return "";
  }

  // Pattern validation
  if (rule.pattern && !rule.pattern.regex.test(value)) {
    return rule.pattern.message;
  }

  // Min length validation
  if (rule.minLength && value.length < rule.minLength.value) {
    return rule.minLength.message;
  }

  // Max length validation
  if (rule.maxLength && value.length > rule.maxLength.value) {
    return rule.maxLength.message;
  }

  // Custom validation function
  if (rule.custom) {
    const customError = rule.custom(value);
    if (customError) return customError;
  }

  // Validate function (returns true or error message)
  if (rule.validate) {
    const customValidate = rule.validate(value);
    if (customValidate !== true) return customValidate;
  }

  // Match field validation (for confirm password, etc.)
  if (rule.matchField && formData[rule.matchField] !== value) {
    return `${name.replace(/_/g, " ")} must match ${rule.matchField.replace(
      /_/g,
      " "
    )}`;
  }

  return "";
};

/**
 * Validate all form fields
 * @param {object} formData - Form data object
 * @param {object} validationConfig - Optional custom validation rules
 * @returns {object} Errors object
 */
export const validateAllFields = (formData, validationConfig = null) => {
  const errors = {};

  Object.keys(formData).forEach((field) => {
    const customRule = validationConfig?.[field];
    const error = validateField(field, formData[field], customRule, formData);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

/**
 * Clean input based on field type
 * @param {string} name - Field name
 * @param {string} value - Field value
 * @returns {string} Cleaned value
 */
// export const cleanInput = (name, value) => {
//   // Phone: only digits
//   if (name.includes('phone')) {
//     return value.replace(/\D/g, "");
//   }

//   // Name fields: only letters, spaces, hyphens, apostrophes
//   if (name.includes('name') && !name.includes('username') && !name.includes('company')) {
//     return value.replace(/[^a-zA-Z\s'-]/g, "");
//   }

//   // Numbers only
//   if (name.includes('age') || name.includes('quantity') || name.includes('pincode') || name.includes('zip')) {
//     return value.replace(/\D/g, "");
//   }

//   // Default: return as is
//   return value;
// };

export const cleanInput = (name, value) => {
  if (value == null) return "";

  // keep leading spaces for now only trimStart is done earlier in hook,
  // but ensure we trim trailing white-space here
  const val = typeof value === "string" ? value.trimEnd() : value;

  switch (name) {
    // Fields that may contain URLs, emails, addresses, notes — keep as-is
    case "service_name":
    case "email":
    case "address":
    case "notes":
      // Return the raw value (optionally you could run a light trim)
      return typeof val === "string" ? val.trim() : val;

    // More restrictive fields — allow letters, numbers and a limited set of punctuation
    case "leadName":
    case "business_category":
    case "industry_field":
      return String(val)
        .replace(/[^a-zA-Z0-9\s&().,'-]/g, "")
        .trim();

    // phone typically only digits (you likely already restrict elsewhere)
    case "phone":
      return String(val).replace(/[^\d]/g, "").trim();

    // Default: just trim whitespace (do not strip punctuation aggressively)
    default:
      return typeof val === "string" ? val.trim() : val;
  }
};

/**
 * Check if form has any errors
 * @param {object} errors - Errors object
 * @returns {boolean}
 */
export const hasErrors = (errors) => {
  return Object.keys(errors).length > 0;
};
